import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mark } from '../ui/Icon';
import { useAuth } from './AuthProvider';

export function SignInScreen({ error }: { error?: string }) {
  const auth = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  return (
    <main className="signin">
      <Mark />
      <h2>Reel Machine</h2>
      <p className="muted">
        From press release to posted reel. Sign in with your work email; we send you a one-time code.
      </p>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          const back = location.pathname.startsWith('/auth/') ? '/' : location.pathname + location.search;
          void auth.signIn(back).catch(() => setBusy(false));
        }}
      >
        {busy ? 'Opening sign-in…' : 'Sign in'}
      </button>
    </main>
  );
}

/** Route guard: shows the sign-in screen until there is a session. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return (
      <main className="signin" role="status">
        <p className="muted">Loading…</p>
      </main>
    );
  }
  if (auth.status === 'signedOut') return <SignInScreen />;
  return <>{children}</>;
}

/** `/auth/callback`: exchanges the code, then continues where the person started. */
export function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    auth
      .finishSignIn(new URLSearchParams(location.search))
      .then((to) => navigate(to, { replace: true }))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.'));
  }, [auth, location.search, navigate]);

  if (error) return <SignInScreen error={error} />;
  return (
    <main className="signin" role="status">
      <p className="muted">Signing in…</p>
    </main>
  );
}
